import React, { useEffect, useMemo, useState } from 'react';
import { KnowledgeProvider, KnowledgeApp } from '@lyra-vault/ui';
import '@lyra-vault/ui/styles.css';
import '@lyra-vault/ui/lyra-theme.css';
import api, { API_ORIGIN } from '../services/api';

// Vault: the user's Knowledge Spaces (documents agents can search), rendered by @lyra-vault/ui through the Lyra embed
// contract. Requests go to Core's /api/integrations/lyra-knowledge, which signs the user in to the knowledge service and
// picks their tenant; this page never holds a service credential.

const useColorMode = (): 'light' | 'dark' => {
  const read = () => (document.documentElement.classList.contains('dark') ? 'dark' : 'light');
  const [mode, setMode] = useState<'light' | 'dark'>(read);
  useEffect(() => {
    const observer = new MutationObserver(() => setMode(read()));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  return mode;
};

type Status = { configured: boolean; keySet: boolean; host: string | null; reachable: boolean; keyOk?: boolean; httpStatus?: number; health?: { status?: string; database?: string; pgvector?: string } | null; problem?: string };

// What is wrong, in the order an admin would fix it. null means the service answers like a healthy knowledge backend.
const diagnose = (s: Status): string | null => {
  if (!s.configured) return 'Core has no KNOWLEDGE_SERVICE_URL. Set it to the knowledge service\'s public URL and redeploy.';
  if (!s.keySet) return 'Core has no KNOWLEDGE_SERVICE_KEY. It must equal LYRA_KNOWLEDGE_SERVICE_API_KEY on the knowledge service.';
  if (s.problem) return `${s.problem} (${s.host}).`;
  if (!s.reachable) return `Core cannot reach ${s.host}.`;
  if (s.health?.status !== 'ok') return `${s.host} answered, but the service reports: database ${s.health?.database ?? '?'}, pgvector ${s.health?.pgvector ?? '?'}.`;
  if (s.keyOk === false && !s.problem) return `${s.host} rejected Core's service key. KNOWLEDGE_SERVICE_KEY must equal LYRA_KNOWLEDGE_SERVICE_API_KEY on the knowledge service.`;
  return null;
};

// A service that answered healthy a few minutes ago is not re-checked in front of the user: the Vault opens at once and the
// check repeats quietly. Only a failing check replaces the page. (Sleeping hosts take up to a minute to wake, so blocking on
// every visit made the Vault feel broken.)
const HEALTHY_KEY = 'lyra:vault:healthy-at';
const FRESH_MS = 10 * 60 * 1000;
const recentlyHealthy = () => { try { return Date.now() - Number(sessionStorage.getItem(HEALTHY_KEY) || 0) < FRESH_MS; } catch { return false; } };
const markHealthy = (ok: boolean) => { try { if (ok) sessionStorage.setItem(HEALTHY_KEY, String(Date.now())); else sessionStorage.removeItem(HEALTHY_KEY); } catch { /* not remembered */ } };

/** Wake the knowledge service ahead of a Vault visit. Fire and forget; call it from a page the user opens first. */
export function warmKnowledgeService() {
  try { if (sessionStorage.getItem('lyra:vault:warmed')) return; sessionStorage.setItem('lyra:vault:warmed', '1'); } catch { /* warm anyway */ }
  api.get('/api/integrations/lyra-knowledge/status').then((r) => markHealthy(diagnose(r.data as Status) === null)).catch(() => { /* the Vault page reports problems */ });
}

export default function VaultPage() {
  const mode = useColorMode();
  const [problem, setProblem] = useState<string | null | undefined>(() => (recentlyHealthy() ? null : undefined)); // undefined: still checking
  const [attempt, setAttempt] = useState(0);
  const [host, setHost] = useState<string | null>(null);   // which knowledge service Core is talking to
  useEffect(() => {
    let live = true;
    if (!recentlyHealthy()) setProblem(undefined);
    api.get('/api/integrations/lyra-knowledge/status')
      .then((r) => { if (!live) return; const found = diagnose(r.data as Status); markHealthy(found === null); setHost((r.data as Status).host || null); setProblem(found); })
      .catch(() => live && setProblem(null)); // the status route itself failing should not hide the page; its own errors will show
    return () => { live = false; };
  }, [attempt]);
  // Whose documents: the user's own, or an organization they belong to in Workforce (read-only here). The server decides what
  // a scope may touch; this only says which one to ask for.
  const [scopes, setScopes] = useState<{ id: string; label: string; readOnly: boolean }[]>([{ id: 'personal', label: 'Personal', readOnly: false }]);
  const [scope, setScope] = useState<string>(() => { try { return localStorage.getItem('lyra:vault:scope') || 'personal'; } catch { return 'personal'; } });
  useEffect(() => {
    api.get('/api/integrations/lyra-knowledge/scopes').then((r) => {
      const list = r.data?.scopes;
      if (Array.isArray(list) && list.length) { setScopes(list); if (!list.some((x: any) => x.id === scope)) setScope('personal'); }
    }).catch(() => { /* personal only */ });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { try { localStorage.setItem('lyra:vault:scope', scope); } catch { /* not remembered */ } }, [scope]);
  const current = scopes.find((x) => x.id === scope) || scopes[0];

  const config = useMemo(() => ({
    apiBase: `${API_ORIGIN}/api/integrations/lyra-knowledge`,
    // Core's own login token; the proxy checks it and replaces it with the service credentials.
    getHeaders: async () => ({ Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`, 'X-Lyra-Scope': current.id }),
  }), [current.id]);

  if (problem) {
    return (
      <div style={{ padding: 24 }}>
        <div className="lyra-embed" data-lyra-theme={mode} style={{ maxWidth: 640, border: '0.5px solid var(--lx-color-border)', borderRadius: 12, padding: 20, background: 'var(--lx-color-surface)', color: 'var(--lx-color-text)' }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>The Vault cannot reach its knowledge service</div>
          <div style={{ color: 'var(--lx-color-text-muted)', fontSize: 14 }}>{problem}</div>
          <button onClick={() => { markHealthy(false); setAttempt((n) => n + 1); }} style={{ marginTop: 12, border: '0.5px solid var(--lx-color-border)', background: 'var(--lx-color-surface)', color: 'var(--lx-color-text)', borderRadius: 10, padding: '7px 14px', fontWeight: 600, cursor: 'pointer' }}>Check again</button>
        </div>
      </div>
    );
  }
  if (problem === undefined) return <div style={{ padding: 24, opacity: 0.6 }}>Checking the knowledge service (a sleeping service can take up to a minute to wake)…</div>;

  return (
    <div style={{ padding: 24, minHeight: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
      <div className="lyra-embed" data-lyra-theme={mode}>
        {scopes.length > 1 && (
          <div role="tablist" aria-label="Whose documents" style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
            {scopes.map((sc) => (
              <button key={sc.id} role="tab" aria-selected={sc.id === current.id} onClick={() => setScope(sc.id)}
                style={{ padding: '6px 14px', borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: `0.5px solid ${sc.id === current.id ? 'var(--app-primary-soft-2)' : 'var(--lx-color-border)'}`, background: sc.id === current.id ? 'var(--app-primary-soft)' : 'var(--lx-color-surface)', color: sc.id === current.id ? 'var(--app-primary-deep)' : 'var(--lx-color-text)' }}>
                {sc.label}
              </button>
            ))}
          </div>
        )}
        {current.readOnly && (
          <div role="status" style={{ marginBottom: 14, padding: '9px 12px', borderRadius: 10, fontSize: 13, background: 'var(--app-primary-soft)', color: 'var(--app-primary-deep)', border: '0.5px solid var(--app-primary-soft-2)' }}>
            These are {current.label}'s documents from Workforce. You can search and ask here; to add or change documents, use Workforce.
            {host && <span style={{ display: 'block', marginTop: 4, fontSize: 12, opacity: 0.85 }}>Reading from <b>{host}</b> as organization <code>{current.id.replace(/^org:/, '')}</code>. Workforce must use this same knowledge service for its spaces to appear.</span>}
          </div>
        )}
        <KnowledgeProvider key={current.id} config={config}>
          <KnowledgeApp />
        </KnowledgeProvider>
      </div>
    </div>
  );
}
